import { LineItem } from "@made-people/grease-cart";
import { ProductWithVariants } from "@made-people/grease-product";
import { useTranslate } from "@made-people/grease-translations";
import { getStockStatus } from "./out-of-stock-information";
import { getProductDiscontinuedStatus } from "./get-stock-delivery-data";
import { useAllocationRule } from "../../../services/centra/allocation-rule/mm-allocation-rule-provider";
import { ProductDeliveryStatus } from "./product-delivery-status";
import { useCartWarehouseStock } from "../../../hooks/use-cart-warehouse-stock";
import { WAREHOUSE_NAMES } from "../../../services/centra/allocation-rule/constants";

export const LineItemDeliveryStatus = ({
  lineItem,
}: {
  lineItem: LineItem;
}) => {
  const translate = useTranslate();
  const {
    allocationRule,
    isLoading: isAllocationRuleLoading,
    hasError: isAllocationRuleError,
  } = useAllocationRule();
  const { stockByVariant } = useCartWarehouseStock();

  if (isAllocationRuleLoading || isAllocationRuleError) {
    return null;
  }

  if (lineItem.type !== "product") {
    return null;
  }

  const product = lineItem.product;
  const productBase = product.base;
  const discontinued = getProductDiscontinuedStatus(productBase);

  if (product.dataModel !== "with_variants" && product.dataModel !== "simple") {
    return (
      <ProductDeliveryStatus
        status={translate("Delivery_In_Stock")}
        isImmediateDelivery
        discontinued={false}
        inStock
      />
    );
  }

  if (product.dataModel === "with_variants") {
    const variant = (product as ProductWithVariants).variants.find(
      (v) => v.variantId === lineItem.id,
    );
    if (!variant) {
      console.warn(
        `[LineItemDeliveryStatus] Variant not found for lineItem.id=${lineItem.id}, product=${productBase.productId}`,
      );
      return (
        <ProductDeliveryStatus
          status={translate("Delivery_In_Stock")}
          isImmediateDelivery
          discontinued={false}
          inStock
        />
      );
    }
  }

  const warehouses = allocationRule?.warehouses ?? [];
  const warehouseIds = warehouses.map((w) => String(w.id));
  const virtualWarehouseId = warehouses.find(
    (w) => w.name === WAREHOUSE_NAMES.VIRTUAL,
  )?.id;

  // Keyed by the line item id (equals variant.variantId for variants and
  // itemId for simple products).
  const stockByWarehouse = stockByVariant[lineItem.id] ?? {};

  if (Object.keys(stockByWarehouse).length === 0) {
    return (
      <ProductDeliveryStatus
        status={translate("Delivery_In_Stock")}
        isImmediateDelivery
        discontinued={false}
        inStock
      />
    );
  }

  const { deliveryStatus, isImmediateDelivery } = getStockStatus(
    {
      discontinued,
      warehouseIds,
      virtualWarehouseId:
        virtualWarehouseId !== undefined
          ? String(virtualWarehouseId)
          : undefined,
      stockByWarehouse,
    },
    translate,
  );

  const inStock = warehouseIds.some(
    (warehouseId) => (stockByWarehouse[warehouseId] ?? 0) > 0,
  );

  return (
    <ProductDeliveryStatus
      status={deliveryStatus}
      isImmediateDelivery={isImmediateDelivery}
      discontinued={discontinued}
      inStock={inStock}
    />
  );
};
